import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import Select from 'react-select'
import {useAppDispatch} from 'src/redux/storeHooks'
import {getCountriesAsync, getStateAsync} from 'src/redux/actions/omsPageActions'
import {AddressState} from './types'

type LocationOption = {value: number; label: string; code?: string}

interface OmsAddressBlockProps {
  title: string
  data: AddressState
  onChange: (field: keyof AddressState, val: string) => void
  onAddressChange: (updates: Partial<AddressState>) => void
  prefix?: 'billing' | 'shipping'
  errors?: Record<string, string>
  disabled?: boolean
  showToggle?: boolean
  toggleChecked?: boolean
  onToggle?: (checked: boolean) => void
  required?: boolean
  inputCls?: string
}

const defaultInputCls = 'oms-input'

const OmsAddressBlock: FC<OmsAddressBlockProps> = ({
  title,
  data,
  onChange,
  onAddressChange,
  prefix = 'billing',
  errors = {},
  disabled = false,
  showToggle = false,
  toggleChecked = false,
  onToggle = () => {},
  required = false,
  inputCls = defaultInputCls,
}) => {
  const dispatch = useAppDispatch()
  const [countryList, setCountryList] = useState<LocationOption[]>([])
  const [stateList, setStateList] = useState<LocationOption[]>([])
  const onAddressChangeRef = useRef(onAddressChange)
  onAddressChangeRef.current = onAddressChange

  useEffect(() => {
    dispatch(getCountriesAsync({})).then((res: any) => {
      const list =
        res?.payload?.map((c: any) => ({
          value: c.id,
          label: c.name,
          code: c.code,
        })) || []
      setCountryList(list)
    })
  }, [dispatch])

  useEffect(() => {
    if (!data.country || data.countryId || countryList.length === 0) return
    const matched = countryList.find(
      (c) => c.label.toLowerCase() === data.country.toLowerCase()
    )
    if (matched) {
      onAddressChangeRef.current({countryId: matched.value})
    }
  }, [data.country, data.countryId, countryList])

  useEffect(() => {
    if (!data.countryId) {
      setStateList([])
      return
    }

    dispatch(getStateAsync({country_id: data.countryId})).then((res: any) => {
      const list =
        res?.payload?.map((s: any) => ({
          value: s.id,
          label: s.name,
        })) || []
      setStateList(list)
    })
  }, [data.countryId, dispatch])

  const selectedCountry = useMemo(
    () => countryList.find((c) => c.label === data.country) || null,
    [countryList, data.country]
  )

  const selectedState = useMemo(
    () =>
      stateList.find((s) => s.label.toLowerCase() === data.state.toLowerCase()) ||
      (data.state ? {value: 0, label: data.state} : null),
    [stateList, data.state]
  )

  return (
    <div className='bg-[#fafafa] border border-[#eef0f2] rounded-[4px] p-[15px]'>
      <div className='flex justify-between items-center mb-[15px]'>
        <div className='text-[12px] font-bold text-[#007bff] uppercase tracking-[0.5px]'>
          {title} {required && <span className='text-danger'>*</span>}
        </div>
        {showToggle && (
          <div className='flex items-center gap-2'>
            <input
              type='checkbox'
              id='same-as-billing'
              checked={toggleChecked}
              onChange={(e) => onToggle(e.target.checked)}
              className='cursor-pointer'
            />
            <label htmlFor='same-as-billing' className='text-[12px] font-semibold text-[#6c757d] cursor-pointer m-0'>
              Same as Billing Address
            </label>
          </div>
        )}
      </div>
      <div className='grid grid-cols-2 gap-3'>
        <div className='col-span-2'>
          <input
            disabled={disabled}
            className={`${inputCls} ${errors[`${prefix}.addr1`] ? 'border-danger' : ''}`}
            placeholder='Address Line 1 *'
            value={data.addr1}
            onChange={(e) => onChange('addr1', e.target.value)}
          />
          {errors[`${prefix}.addr1`] && (
            <div className='text-danger text-[11px] mt-1'>{errors[`${prefix}.addr1`]}</div>
          )}

          <input
            disabled={disabled}
            className={`${inputCls} mt-2`}
            placeholder='Address Line 2 (Optional)'
            value={data.addr2}
            onChange={(e) => onChange('addr2', e.target.value)}
          />
        </div>

        <div>
          <input
            disabled={disabled}
            className={`${inputCls} ${errors[`${prefix}.city`] ? 'border-danger' : ''}`}
            placeholder='City *'
            value={data.city}
            onChange={(e) => onChange('city', e.target.value)}
          />
          {errors[`${prefix}.city`] && (
            <div className='text-danger text-[11px] mt-1'>{errors[`${prefix}.city`]}</div>
          )}
        </div>

        <div>
          <Select<LocationOption, false>
            classNamePrefix='oms-rs'
            isDisabled={disabled}
            isSearchable
            menuPlacement='auto'
            options={stateList}
            placeholder='State/Region *'
            value={selectedState}
            onChange={(opt) => onAddressChange({state: opt?.label || ''})}
          />
          {errors[`${prefix}.state`] && (
            <div className='text-danger text-[11px] mt-1'>{errors[`${prefix}.state`]}</div>
          )}
        </div>

        <div>
          <input
            disabled={disabled}
            className={`${inputCls} ${errors[`${prefix}.zip`] ? 'border-danger' : ''}`}
            placeholder='Zip / Postal Code *'
            value={data.zip}
            onChange={(e) => onChange('zip', e.target.value)}
          />
          {errors[`${prefix}.zip`] && (
            <div className='text-danger text-[11px] mt-1'>{errors[`${prefix}.zip`]}</div>
          )}
        </div>

        <div>
          <Select<LocationOption, false>
            classNamePrefix='oms-rs'
            isDisabled={disabled}
            isSearchable
            menuPlacement='auto'
            options={countryList}
            placeholder='Country *'
            value={selectedCountry}
            onChange={(opt) =>
              onAddressChange({
                country: opt?.label || '',
                countryId: opt?.value,
                state: '',
              })
            }
          />
          {errors[`${prefix}.country`] && (
            <div className='text-danger text-[11px] mt-1'>{errors[`${prefix}.country`]}</div>
          )}
        </div>
      </div>
    </div>
  )
}

export default OmsAddressBlock
